import type { TFunction } from 'i18next'

import {
  roleInUse,
  roleIsSuperuser,
  roleIsSystem,
  roleKeyFixed,
  roleLastSuperAdmin,
} from '@/features/settings/api/useRoles'
import { fieldErrorKey, fieldErrors } from '@/lib/problems'
import { refusalMessage } from '@/lib/refusals'

/* How the role editor words a refused write (S-13), mirroring
 * `userRefusals.ts`.
 *
 * Three kinds of refusal arrive (identity/roles_handlers.go `fail`):
 *
 *   422 `validation-failed` with a code under `key`, `name` or `description`
 *     (`taken`, `invalid`, `required`, `too-long`) — worded under that field
 *     by `roleFieldError`;
 *   422 with a code under `scopes` or `capabilities` (`superuser-scopes-fixed`,
 *     `unknown-surface`, `reserved`, `unknown-capability`) — worded in the
 *     sheet's banner, because the superuser's editor has no scope section to
 *     put the sentence under (story B3) and the box `reserved` names is already
 *     locked (S-7, O8);
 *   409 `conflict` named by its `detail` token (the http-api.md Conflict
 *     contract; predicates in `api/useRoles.ts`) — `role-in-use`,
 *     `system-role`, `system-role-key-fixed`, `superuser-role`,
 *     `last-super-admin`. All but the first are reachable from this screen
 *     only from a stale tab: the controls that would cause them are absent.
 *
 * Everything else goes through `refusalMessage`, whose 422 sentence is the
 * generic "check the highlighted fields". Never the problem's `detail` or
 * `title`: `ApiError.message` is one of those, which is how a duplicate key
 * once read "Validation failed" and a refused amend printed its token. */

/** The sentence for a refused role write, for the sheet's banner or the
 *  Banner above the role list. */
export function roleRefusalMessage(error: unknown, t: TFunction): string {
  const fields = fieldErrors(error)
  const code = fields['scopes']?.[0] ?? fields['capabilities']?.[0]
  if (code) return t(fieldErrorKey(code))
  if (roleInUse(error)) return t('settings.roles.refusals.roleInUse')
  if (roleIsSystem(error)) return t('settings.roles.refusals.systemRole')
  if (roleKeyFixed(error)) return t('settings.roles.refusals.systemRoleKeyFixed')
  if (roleIsSuperuser(error)) return t('settings.roles.refusals.superuserRole')
  if (roleLastSuperAdmin(error)) return t('settings.roles.refusals.lastSuperAdmin')
  return refusalMessage(error, t)
}

/** The worded field error under one of the role form's text fields, if any. */
export function roleFieldError(
  error: unknown,
  field: 'key' | 'name' | 'description',
  t: TFunction,
): string | undefined {
  const code = fieldErrors(error)[field]?.[0]
  return code ? t(fieldErrorKey(code)) : undefined
}
